import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { Reveal, MaskedLines } from "./Reveal";
import { WHATSAPP_URL } from "../data/content";
import { useLang, useContent } from "../i18n";

export const usePageTitle = (title) => {
  useEffect(() => {
    document.title = title ? `${title} — DOC TOGO` : "DOC TOGO — Vos démarches au Togo, en toute simplicité";
  }, [title]);
};

export const FlagDot = ({ className = "" }) => (
  <span
    className={`relative inline-block shrink-0 overflow-hidden rounded-[3px] ring-1 ring-black/15 ${className}`}
    style={{ width: 22, height: 15 }}
    role="img"
    aria-label="Drapeau du Togo"
  >
    <span
      className="absolute inset-0"
      style={{ background: "linear-gradient(180deg, #006A4E 0 20%, #FFCE00 20% 40%, #006A4E 40% 60%, #FFCE00 60% 80%, #006A4E 80% 100%)" }}
    />
    <span className="absolute left-0 top-0 grid place-items-center bg-[#D21034]" style={{ width: "44%", height: "60%" }}>
      <svg viewBox="0 0 24 24" className="h-[9px] w-[9px] fill-white" aria-hidden="true">
        <path d="M12 1.8l2.6 7.2h7.6l-6.1 4.6 2.4 7.4L12 16.3l-6.5 4.7 2.4-7.4L1.8 9h7.6z" />
      </svg>
    </span>
  </span>
);

export const SectionHeading = ({ eyebrow, title, sub, dark = false, align = "left" }) => (
  <Reveal className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    {eyebrow && (
      <p className={`mb-4 flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.3em] ${align === "center" ? "justify-center" : ""} ${dark ? "text-gold" : "text-brand"}`}>
        <FlagDot /> {eyebrow}
      </p>
    )}
    <h2 className={`font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08] text-balance ${dark ? "text-white" : "text-slate-900"}`}>
      {title}
    </h2>
    {sub && <p className={`mt-5 text-base sm:text-lg leading-relaxed ${dark ? "text-white/60" : "text-slate-600"}`}>{sub}</p>}
  </Reveal>
);

export const CTABand = ({ title, sub }) => {
  const { t } = useLang();
  const finalTitle = title || t("ctaband.title");
  const finalSub = sub || t("ctaband.sub");
  return (
  <section data-testid="cta-band" className="relative overflow-hidden bg-brand-deep py-20 sm:py-24">
    <div className="pointer-events-none absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-gold/15 blur-[120px]" />
    <div className="pointer-events-none absolute -bottom-32 right-1/4 h-96 w-96 rounded-full bg-brand-muted/40 blur-[120px]" />
    <div className="relative mx-auto max-w-7xl px-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10">
      <Reveal className="max-w-2xl">
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white text-balance">{finalTitle}</h2>
        <p className="mt-4 text-base sm:text-lg text-white/60 leading-relaxed">{finalSub}</p>
      </Reveal>
      <Reveal delay={0.15} className="flex flex-col sm:flex-row gap-4 shrink-0">
        <Link
          to="/contact"
          data-testid="cta-band-demarrer"
          className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-display font-bold text-brand-deep transition-[background-color,transform] duration-300 hover:bg-gold-warm hover:-translate-y-0.5"
        >
          {t("cta.start")}
          <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="cta-band-whatsapp"
          className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-8 py-4 font-display font-bold text-white transition-[background-color,border-color] duration-300 hover:bg-white/10 hover:border-white/40"
        >
          <MessageCircle className="h-5 w-5 text-[#25D366]" />
          {t("cta.whatsappShort")}
        </a>
      </Reveal>
    </div>
  </section>
  );
};

export const ProcessSection = ({ dark = true }) => {
  const { t } = useLang();
  const { STEPS } = useContent();
  return (
  <section data-testid="process-section" className={`py-24 sm:py-28 ${dark ? "bg-brand-deep" : "bg-ivory"}`}>
    <div className="mx-auto max-w-7xl px-6">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
        <SectionHeading
          dark={dark}
          eyebrow={t("process.eyebrow")}
          title={<>{t("process.title1")}<br />{t("process.title2")}</>}
          sub={t("process.sub")}
        />
        <Reveal delay={0.2}>
          <Link
            to="/contact"
            data-testid="process-cta"
            className="group inline-flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.25em] text-gold"
          >
            {t("process.cta")}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div>
        {STEPS.map((step, i) => (
          <Reveal key={step.num} delay={i * 0.06}>
            <div
              data-testid={`process-step-${step.num}`}
              className={`group grid grid-cols-[auto_1fr] lg:grid-cols-[180px_1fr_1.2fr] gap-x-6 gap-y-2 items-baseline border-t py-8 lg:py-10 transition-colors duration-500 ${
                dark ? "border-white/10 hover:border-gold/40" : "border-slate-200 hover:border-brand/40"
              }`}
            >
              <span className={`font-mono text-4xl lg:text-6xl font-bold transition-colors duration-500 ${dark ? "text-white/15 group-hover:text-gold" : "text-brand/15 group-hover:text-brand"}`}>
                {step.num}
              </span>
              <h3 className={`font-display text-xl lg:text-2xl font-bold ${dark ? "text-white" : "text-slate-900"}`}>{step.title}</h3>
              <p className={`col-start-2 lg:col-start-3 text-sm lg:text-base leading-relaxed ${dark ? "text-white/50" : "text-slate-600"}`}>{step.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

export const PageHero = ({ eyebrow, lines, sub, image, imageAlt }) => {
  const { t } = useLang();
  return (
  <section data-testid="page-hero" className="relative overflow-hidden bg-brand-deep text-white">
    <div className="pointer-events-none absolute -top-32 right-[-5%] h-96 w-96 rounded-full bg-gold/15 blur-[110px]" />
    <div className="relative mx-auto max-w-7xl px-6 pt-32 pb-16 lg:pt-40 lg:pb-20 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <div className="mb-5 flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.3em] text-gold">
          <FlagDot /> {eyebrow}
        </div>
        <MaskedLines
          lines={lines}
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.06] text-balance"
        />
        {sub && <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-white/60">{sub}</p>}
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <Link
            to="/contact"
            data-testid="page-hero-cta"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-3.5 font-display font-bold text-brand-deep transition-[background-color,transform] duration-300 hover:bg-gold-warm hover:-translate-y-0.5"
          >
            {t("cta.start")}
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="page-hero-whatsapp"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-7 py-3.5 font-display font-bold text-white transition-[background-color] duration-300 hover:bg-white/10"
          >
            <MessageCircle className="h-5 w-5 text-[#25D366]" />
            {t("cta.whatsappShort")}
          </a>
        </div>
      </div>
      {image && (
        <Reveal delay={0.25} className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -inset-3 rounded-[2rem] border border-gold/25 rotate-2" />
            <img src={image} alt={imageAlt || "DOC TOGO"} loading="lazy" className="relative h-72 lg:h-96 w-full rounded-[2rem] object-cover shadow-2xl" />
          </div>
        </Reveal>
      )}
    </div>
  </section>
  );
};
